import React, { useEffect, useState, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import './UniversalModal.css';
import { pushModalToStack, removeModalFromStack, isTopModal } from './modalStack';
import UnsavedChangesPopover from './UnsavedChangesPopover';

const UniversalModal = ({ 
  isOpen, 
  onClose, 
  title,
  isViewMode = false, 
  onEdit,
  onSave,
  onSaveAndExit,
  onCancel,
  children,
  hasChanges = false,
  onConfirmClose,
  disableEscClose = false,
  modalClassName = '',
  bodyClassName = ''
}) => {
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const modalIdRef = useRef(`modal_${Date.now()}_${Math.random().toString(16).slice(2)}`);
  const overlayLeftMouseDownRef = useRef(false);

  const requestClose = useCallback(() => {
    if (hasChanges && !isViewMode) {
      setShowConfirmDialog(true);
      return;
    }
    onClose();
  }, [hasChanges, isViewMode, onClose]);

  const handleConfirmClose = useCallback(() => {
    setShowConfirmDialog(false);
    // Если родитель передал обработчик "закрыть без сохранения" — используем его, иначе закрываем как обычно.
    if (onConfirmClose) {
      onConfirmClose();
      return;
    }
    onClose();
  }, [onConfirmClose, onClose]);

  useEffect(() => {
    const id = modalIdRef.current;
    if (isOpen) pushModalToStack(id);
    return () => removeModalFromStack(id);
  }, [isOpen]);

  useEffect(() => {
    const handleEscape = (e) => {
      if (disableEscClose) return;
      if (e.key === 'Escape' && isOpen) {
        // ESC обрабатывает только верхняя (последняя открытая) модалка.
        if (!isTopModal(modalIdRef.current)) return;

        // Не закрываем модалку, если активен полноэкранный редактор
        try {
          if (document.querySelector('.tiptap-editor-wrapper.fullscreen-mode')) {
            e.preventDefault();
            e.stopPropagation();
            if (e.stopImmediatePropagation) e.stopImmediatePropagation();
            document.dispatchEvent(new Event('requestExitFullscreen'));
            return;
          }
        } catch(_) {}
        // Если открыт диалог подтверждения — ESC закрывает его, а не основную модалку.
        if (showConfirmDialog) {
          e.preventDefault();
          e.stopPropagation();
          if (e.stopImmediatePropagation) e.stopImmediatePropagation();
          setShowConfirmDialog(false);
          return;
        }
        // Если открыт календарь — закрываем его, а не модалку.
        try {
          if (document.body.classList.contains('aot-calendar-open')) {
            e.preventDefault();
            e.stopPropagation();
            if (e.stopImmediatePropagation) e.stopImmediatePropagation();
            document.dispatchEvent(new Event('aot-close-calendar'));
            return;
          }
        } catch (_) {}
        // Если открыт выпадающий searchable-select, ESC закрывает его, а не всю модалку.
        try {
          if (document.body.classList.contains('aot-searchable-select-open')) {
            e.preventDefault();
            e.stopPropagation();
            if (e.stopImmediatePropagation) e.stopImmediatePropagation();
            document.dispatchEvent(new Event('aot-close-searchable-select'));
            return;
          }
        } catch (_) {}
        e.preventDefault();
        e.stopPropagation();
        if (e.stopImmediatePropagation) e.stopImmediatePropagation();
        requestClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape, { capture: true });
    }

    return () => {
      document.removeEventListener('keydown', handleEscape, { capture: true });
    };
  }, [isOpen, requestClose, showConfirmDialog, disableEscClose]);

  if (!isOpen) return null;

  const handleOverlayMouseDown = (e) => {
    // Закрываем по клику на фон только если нажатие началось на фоне ЛКМ.
    overlayLeftMouseDownRef.current = e.target === e.currentTarget && e.button === 0;
  };

  const handleOverlayClick = (e) => {
    if (e.target !== e.currentTarget) return;
    if (!overlayLeftMouseDownRef.current) return;
    overlayLeftMouseDownRef.current = false;
    requestClose();
  };

  const modalContent = (
    <div
      className="universal-modal-overlay"
      onMouseDown={handleOverlayMouseDown}
      onClick={handleOverlayClick}
      onContextMenu={() => { overlayLeftMouseDownRef.current = false; }}
    >
      <div className={`universal-modal ${modalClassName}`} onClick={(e) => e.stopPropagation()}>
        <div className="universal-modal-header">
          <h2>{title}</h2>
          <div className="universal-modal-header-actions">
            {isViewMode && onEdit && (
              <button type="button" className="btn btn-primary" onClick={onEdit}>
                Редактировать
              </button>
            )}
            <button className="universal-modal-close" onClick={requestClose}>
              <span>&times;</span>
            </button>
          </div>
        </div>
        <div className={`universal-modal-body ${bodyClassName}`}>
          {children}
        </div>
        {!isViewMode && (onSave || onSaveAndExit || onCancel) && (
          <div className="universal-modal-footer">
            {onSave && (
              <button type="button" className="btn btn-primary" onClick={onSave}>
                Сохранить
              </button>
            )}
            {onSaveAndExit && (
              <button type="button" className="btn btn-primary" onClick={onSaveAndExit}>
                Сохранить и закрыть
              </button>
            )}
            {(onCancel || onClose) && (
              <button type="button" className="btn btn-secondary" onClick={onCancel || requestClose}>
                Отмена
              </button>
            )}
          </div>
        )}
      </div>

      <UnsavedChangesPopover
        isOpen={showConfirmDialog}
        onCancel={() => setShowConfirmDialog(false)}
        onConfirm={handleConfirmClose}
        zIndex={13000}
      />
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default UniversalModal;
